import React from 'react';
import { useDispatch } from 'react-redux';

import Content from '../../components/Content';
import Loader from '../../components/Loader';
import Table, { SortableTh } from '../../components/Table';
import useSort from '../../components/Table/useSort';
import SelectBox from '../../components/SelectBox';
import NetworkError from '../../components/Error';
import Pagination from '../../components/Pagination';
import usePagination from '../../components/Pagination/usePagination';
import { ErrorCodes } from '../../utils/errorCodes';
import { formatFrontendSupportError } from '../../utils/frontendErrorFormat';

import {
	getSocAnalytics, getSocAllowlist, addSocAllowlist, deleteSocAllowlist,
} from '../../redux/actions/watcherActions';

import styles from './SocAnalytics.module.css';

// SOC Analytics — behavioural detections computed centrally over the telemetry
// the appliances already ship. Read-only review queue: a human triages findings,
// nothing here auto-raises an alert. A per-client allowlist suppresses known-good
// findings. See docs/SOC-MONITORING-IDEAS.md.

// Per-detector columns: header label + the finding field it reads. A trailing
// Watcher column is appended for every detector.
const COLUMNS = {
	dns: [
		{ h: 'Severity', f: 'severity', sev: true }, { h: 'Type', f: 'type' },
		{ h: 'Device', f: 'device', mono: true }, { h: 'Domain', f: 'domain', mono: true, wrap: true },
		{ h: 'Detail', f: 'detail' }, { h: 'Hits', f: 'hits' }, { h: 'Last Seen', f: 'lastSeen' },
	],
	beacon: [
		{ h: 'Severity', f: 'severity', sev: true }, { h: 'Source(s)', f: 'source', mono: true },
		{ h: 'External Host', f: 'host', mono: true }, { h: 'Owner', f: 'owner', wrap: true },
		{ h: 'ASN', f: 'asn', mono: true }, { h: 'Port', f: 'port' },
		{ h: 'Country', f: 'country' }, { h: 'Hits', f: 'hits' },
		{ h: 'Interval', f: 'interval' }, { h: 'Regularity', f: 'regularity' }, { h: 'CV', f: 'cv' },
		{ h: 'Window', f: 'window' }, { h: 'Last Seen', f: 'lastSeen' },
	],
	ot: [
		{ h: 'Severity', f: 'severity', sev: true }, { h: 'Status', f: 'status' },
		{ h: 'Protocol', f: 'protocol' }, { h: 'Direction', f: 'direction' },
		{ h: 'Sender', f: 'sender', mono: true }, { h: 'Responder', f: 'responder', mono: true },
		{ h: 'Fn', f: 'function' }, { h: 'Command', f: 'command', wrap: true }, { h: 'Observed', f: 'observedAt' },
	],
	otnew: [
		{ h: 'Severity', f: 'severity', sev: true }, { h: 'Device', f: 'device', mono: true },
		{ h: 'Protocols', f: 'protocols' }, { h: 'Commands', f: 'commands', wrap: true },
		{ h: 'Targets', f: 'targets' }, { h: 'First Seen', f: 'firstSeen' },
	],
	scan: [
		{ h: 'Severity', f: 'severity', sev: true }, { h: 'Type', f: 'type' },
		{ h: 'Source', f: 'source', mono: true }, { h: 'Target', f: 'target', mono: true },
		{ h: 'Count', f: 'count' }, { h: 'Detail', f: 'detail' },
		{ h: 'Window', f: 'window' }, { h: 'Last Seen', f: 'lastSeen' },
	],
	geoanom: [
		{ h: 'Severity', f: 'severity', sev: true }, { h: 'Source IP', f: 'source', mono: true },
		{ h: 'Destination IP', f: 'destination', mono: true }, { h: 'Host', f: 'host', mono: true, wrap: true },
		{ h: 'Owner', f: 'owner', wrap: true }, { h: 'ASN', f: 'asn', mono: true },
		{ h: 'Port', f: 'port', mono: true }, { h: 'Proto', f: 'protocol' },
		{ h: 'Country', f: 'country' }, { h: 'Zones', f: 'srcZone', zone: true },
		{ h: 'First Seen', f: 'firstSeen' },
	],
	newext: [
		{ h: 'Severity', f: 'severity', sev: true }, { h: 'Type', f: 'type' },
		{ h: 'Source', f: 'source', mono: true }, { h: 'Country', f: 'country' },
		{ h: 'Owner(s)', f: 'owner', wrap: true },
		{ h: 'Destinations', f: 'destinations' }, { h: 'Ports', f: 'ports', mono: true },
		{ h: 'Detail', f: 'detail', wrap: true }, { h: 'First Seen', f: 'firstSeen' },
	],
	newsvc: [
		{ h: 'Severity', f: 'severity', sev: true }, { h: 'Source', f: 'source', mono: true },
		{ h: 'Service', f: 'service' }, { h: 'Port', f: 'port' },
		{ h: 'Country', f: 'country' }, { h: 'Destinations', f: 'destinations' },
		{ h: 'Detail', f: 'detail', wrap: true }, { h: 'First Seen', f: 'firstSeen' },
	],
	insecure: [
		{ h: 'Severity', f: 'severity', sev: true }, { h: 'Host', f: 'host', mono: true },
		{ h: 'Port', f: 'port' }, { h: 'Service', f: 'service' },
		{ h: 'Detail', f: 'detail' }, { h: 'Last Seen', f: 'lastSeen' },
	],
	offhours: [
		{ h: 'Severity', f: 'severity', sev: true }, { h: 'Type', f: 'type' },
		{ h: 'Source', f: 'source', mono: true }, { h: 'Activity', f: 'activity', wrap: true },
		{ h: 'Local Time', f: 'localTime' }, { h: 'Observed', f: 'observedAt' },
	],
	exfil: [
		{ h: 'Severity', f: 'severity', sev: true }, { h: 'Type', f: 'type' },
		{ h: 'Source', f: 'source', mono: true }, { h: 'External Host', f: 'host', mono: true },
		{ h: 'Owner', f: 'owner', wrap: true }, { h: 'ASN', f: 'asn', mono: true },
		{ h: 'Country', f: 'country' }, { h: 'Bytes Out', f: 'bytesOut' },
		{ h: 'Bytes In', f: 'bytesIn' }, { h: 'Ratio', f: 'ratio' }, { h: 'Last Seen', f: 'lastSeen' },
	],
	throughput: [
		{ h: 'Severity', f: 'severity', sev: true }, { h: 'Device', f: 'source', mono: true },
		{ h: 'Last Hour', f: 'bytes' }, { h: 'Baseline', f: 'baseline' },
		{ h: 'vs Baseline', f: 'ratio' }, { h: 'Hour', f: 'lastSeen' },
	],
};

// "Source zone → External" for a geo-anomaly row. The backend resolves the
// source IP against the client's net_zones and the destination is external by
// definition; only render when at least one end resolved.
const ZoneChip = ({ name, color }) => {
	if (!name) return null;
	return (
		<span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
			<i style={{ width: 7, height: 7, borderRadius: '50%', flex: 'none',
				background: color || 'var(--text-secondary, #6b7280)' }} />
			{name}
		</span>
	);
};
const ZoneFlow = ({ f }) => {
	if (!f || (!f.srcZone && !f.dstZone)) return '—';
	return (
		<span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
			<ZoneChip name={f.srcZone} color={f.srcZoneColor} />
			{f.srcZone && f.dstZone && <span style={{ opacity: 0.6 }}>→</span>}
			<ZoneChip name={f.dstZone} color={f.dstZoneColor} />
		</span>
	);
};

const INTRO = {
	dns: 'Algorithmically-generated (DGA) and tunneling domains — high-entropy labels, subdomain fan-out, and abnormally long labels in DNS.',
	beacon: 'Sustained periodic callbacks to a single external endpoint. Cadence is approximated from connection frequency (the source data is aggregated).',
	ot: 'ICS/OT control activity that was disapproved, or write-style commands whose approval was never checked (Modbus/BACnet/S7 — only DNP3 is checked today).',
	otnew: 'A device seen speaking an OT control protocol (Modbus/DNP3/BACnet/S7) for the first time in 48h — a new or unexpected node on the control network, or a host that was never an approved controller suddenly issuing control traffic. Raises a High alert.',
	scan: 'Vertical (one host, many ports) and horizontal (one port, many hosts) fan-out — recon and lateral movement, last 24h.',
	geoanom: 'New destination country per source, read from per-connection flow accounting (last 48h) — the same source the dashboard’s "Baseline geo anomaly" uses. One row per source → destination IP : port, showing the destination’s owner and AS number (e.g. AS8075 Microsoft, so a hit in Singapore reads as its real owner, not just a foreign IP), the source’s network zone → External, and the country. Unlike New External (which reads DNS-resolved connections only), this catches direct-to-IP foreign traffic too.',
	newext: 'A source reaching a destination country it had not reached before — one row per (source, country), not per destination. Countries the host already talks to are not re-flagged; the destinations reached are shown as a count and a sample.',
	newsvc: 'An internal device using a service port outbound to the internet that it has never used before — e.g. a workstation that suddenly speaks SSH or RDP externally. Ubiquitous egress (web/DNS/NTP) is excluded and brand-new devices are skipped, so only a genuinely new service type for an established device surfaces. Sensitive ports (remote-access, DB, proxy/Tor) score High and raise an alert.',
	insecure: 'Cleartext / legacy services observed in use (Telnet, FTP, SNMP, …), last 7 days.',
	offhours: 'Control activity outside business hours, read in each appliance’s own timezone (default 20:00–06:00 local plus weekends — tunable).',
	exfil: 'Data exfiltration by volume, from per-connection flow accounting (Tier 3): large outbound transfer to one destination, or an upload-heavy byte ratio. Last 24h.',
	throughput: 'A device whose traffic in the last complete hour far exceeds its own recent hourly baseline (Tier 3) — a volume spike in either direction. Baseline is the device’s average hour over the prior week. Exfil and spikes both raise an alert.',
};

// What a per-row "Allowlist" action suppresses, per detector: the kind + the
// finding field that carries the value to allow.
const QUICK_ALLOW = {
	dns: { kind: 'domain', f: 'domain' },
	beacon: { kind: 'dst_ip', f: 'ip' },
	ot: { kind: 'dst_ip', f: 'responder' },
	otnew: { kind: 'src_ip', f: 'device' },
	scan: { kind: 'src_ip', f: 'source' },
	geoanom: { kind: 'dst_country', f: 'country' },
	newext: { kind: 'dst_country', f: 'country' },
	newsvc: { kind: 'src_ip', f: 'source' },
	insecure: { kind: 'port', f: 'port' },
	offhours: { kind: 'src_ip', f: 'source' },
	exfil: { kind: 'dst_ip', f: 'ip' },
	throughput: { kind: 'src_ip', f: 'source' },
};

const SocAnalyticsScreen = () => {
	const dispatch = useDispatch();

	const [selectedWatcher, setSelectedWatcher] = React.useState('');
	const [searchKey, setSearchKey] = React.useState('');
	const [loading, setLoading] = React.useState(false);
	const [watchers, setWatchers] = React.useState([]);
	const [detectors, setDetectors] = React.useState({});
	const [order, setOrder] = React.useState([]);
	const [summary, setSummary] = React.useState(null);
	const [active, setActive] = React.useState('dns');
	const [hasError, setHasError] = React.useState(false);
	const [supportErrorMessage, setSupportErrorMessage] = React.useState('');

	// Allowlist state.
	const [panelOpen, setPanelOpen] = React.useState(false);
	const [allowRows, setAllowRows] = React.useState([]);
	const [kinds, setKinds] = React.useState([]);
	const [companies, setCompanies] = React.useState([]);
	const [isProvider, setIsProvider] = React.useState(false);
	const [form, setForm] = React.useState({ company: '', kind: 'domain', value: '', note: '' });
	const [allowMsg, setAllowMsg] = React.useState(null);
	const [version, setVersion] = React.useState(0); // bump to refetch after edits

	// Watcher dropdown options, loaded once on mount independent of any selection.
	// An empty watcher makes the backend return just the list (no detector work),
	// so the dropdown populates while the page still shows no data until a pick.
	React.useEffect(() => {
		let alive = true;
		(async () => {
			const data = await dispatch(getSocAnalytics(''));
			if (alive && data && Array.isArray(data.watchers)) setWatchers(data.watchers);
		})();
		return () => { alive = false; };
	}, [dispatch]);

	// Analytics (re-runs when the watcher or version changes).
	React.useEffect(() => {
		let alive = true;
		(async () => {
			if (!selectedWatcher) return;   // nothing selected yet — render with no data
			setLoading(true);
			setDetectors({}); setSummary(null);   // clear previous watcher's findings on switch
			const data = await dispatch(getSocAnalytics(selectedWatcher));
			if (!alive) return;
			if (data && data.status) {
				setDetectors(data.detectors || {});
				setOrder(Array.isArray(data.order) ? data.order : Object.keys(data.detectors || {}));
				setSummary(data.summary || null);
				if (Array.isArray(data.watchers)) setWatchers(data.watchers);
				setHasError(false);
				setSupportErrorMessage('');
			} else {
				setHasError(true);
				setSupportErrorMessage(formatFrontendSupportError({
					errorCode: ErrorCodes.API_INTERNAL_ERROR,
					pageInfo: 'SOC Analytics Page / Load Failed',
					sourceMessage: (data && data.msg) || 'Analytics request did not return success',
				}));
			}
			setLoading(false);
		})();
		return () => { alive = false; };
	}, [selectedWatcher, version, dispatch]);

	// Allowlist (global to the caller; re-runs on version bump).
	React.useEffect(() => {
		let alive = true;
		(async () => {
			const data = await dispatch(getSocAllowlist(''));
			if (!alive || !data || !data.status) return;
			setAllowRows(Array.isArray(data.AllowRows) ? data.AllowRows : []);
			setKinds(Array.isArray(data.kinds) ? data.kinds : []);
			setIsProvider(!!data.isProvider);
			const avail = (data.availableCompanies || []).filter((c) => c !== 'ALL');
			setCompanies(avail);
			setForm((f) => ({ ...f, company: f.company || avail[0] || '' }));
		})();
		return () => { alive = false; };
	}, [version, dispatch]);

	const activeDetector = detectors[active] || { findings: [], label: '', count: 0 };
	const cols = COLUMNS[active] || [];

	const filtered = React.useMemo(() => {
		const key = searchKey.trim().toLowerCase();
		const rows = activeDetector.findings || [];
		if (!key) return rows;
		return rows.filter((f) => Object.values(f).some((v) => String(v).toLowerCase().includes(key)));
	}, [activeDetector, searchKey]);

	const sort = useSort(filtered);
	const { pageRows, pageNumber, totalRows, pageLimit, handlePageChange } = usePagination(sort.sorted);

	const sevClass = (sev) => styles['sev_' + String(sev || '').toLowerCase()] || '';

	const submitAllow = async () => {
		if (!form.value.trim()) { setAllowMsg({ ok: false, text: 'A value is required.' }); return; }
		const res = await dispatch(addSocAllowlist(form));
		if (res && res.status) {
			setAllowMsg({ ok: true, text: res.msg || 'Added.' });
			setForm((f) => ({ ...f, value: '', note: '' }));
			setVersion((v) => v + 1);
		} else {
			setAllowMsg({ ok: false, text: (res && res.msg) || 'Failed to add entry.' });
		}
	};

	const removeAllow = async (id) => {
		const res = await dispatch(deleteSocAllowlist(id));
		if (res && res.status) { setAllowMsg({ ok: true, text: res.msg || 'Removed.' }); setVersion((v) => v + 1); }
		else setAllowMsg({ ok: false, text: (res && res.msg) || 'Failed to remove entry.' });
	};

	// Pre-fill the allowlist form from a finding and open the panel.
	const quickAllow = (finding) => {
		const q = QUICK_ALLOW[active];
		if (!q) return;
		setForm((f) => ({ ...f, kind: q.kind, value: String(finding[q.f] ?? '') }));
		setAllowMsg(null);
		setPanelOpen(true);
		if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
	};

	return (
		<Content
			currentMenu='soc-analytics'
			headerTitle='SOC Analytics'
			searchBox={true}
			searchPlaceholder='Search findings'
			searchValue={searchKey}
			searchOnChange={(e) => setSearchKey(e.target.value)}
		>
			<div className={styles.intro}>
				Behavioural detections computed over the telemetry your appliances already ship —
				a read-only review queue for the SOC. Nothing here raises an alert on its own.
			</div>

			{summary && (
				<div className={styles.summary_grid}>
					<div className={styles.summary_card}>
						<span className={styles.summary_value}>{summary.total}</span>
						<span className={styles.summary_label}>Total Findings</span>
					</div>
					<div className={`${styles.summary_card} ${styles.high}`}>
						<span className={styles.summary_value}>{summary.high}</span>
						<span className={styles.summary_label}>High Severity</span>
					</div>
					<div className={styles.summary_card}>
						<span className={styles.summary_value}>{allowRows.length}</span>
						<span className={styles.summary_label}>Allowlist Entries</span>
					</div>
					<button className={styles.allow_toggle} onClick={() => setPanelOpen((o) => !o)}>
						{panelOpen ? 'Hide allowlist' : 'Manage allowlist'}
					</button>
				</div>
			)}

			{panelOpen && (
				<div className={styles.allow_panel}>
					<h4>Per-client allowlist</h4>
					<p className={styles.allow_hint}>
						Suppress known-good findings. A matching entry drops a finding before it
						reaches this queue — tune here before promoting a detector to alerting.
					</p>
					{allowMsg && (
						<p className={`${styles.allow_msg} ${allowMsg.ok ? styles.ok : styles.err}`}>{allowMsg.text}</p>
					)}
					<div className={styles.allow_form}>
						{isProvider && (
							<div className={styles.allow_field}>
								<label>Client</label>
								<select value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })}>
									{companies.map((c) => <option key={c} value={c}>{c}</option>)}
								</select>
							</div>
						)}
						<div className={styles.allow_field}>
							<label>Kind</label>
							<select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
								{kinds.map((k) => <option key={k[0]} value={k[0]}>{k[1]}</option>)}
							</select>
						</div>
						<div className={styles.allow_field}>
							<label>Value</label>
							<input value={form.value} placeholder='e.g. example.com / 10.0.0.5 / 23'
								onChange={(e) => setForm({ ...form, value: e.target.value })} />
						</div>
						<div className={styles.allow_field}>
							<label>Note</label>
							<input value={form.note} placeholder='optional'
								onChange={(e) => setForm({ ...form, note: e.target.value })} />
						</div>
						<button className={styles.allow_add} onClick={submitAllow}>Add</button>
					</div>

					<table className={styles.allow_table}>
						<thead>
							<tr><th>Kind</th><th>Value</th><th>Note</th>{isProvider && <th>Client</th>}<th></th></tr>
						</thead>
						<tbody>
							{allowRows.length > 0 ? allowRows.map((r) => (
								<tr key={r[0]}>
									<td>{r[2]}</td>
									<td className={styles.mono}>{r[3]}</td>
									<td>{r[4] || '—'}</td>
									{isProvider && <td>{r[5]}</td>}
									<td><button className={styles.allow_del} onClick={() => removeAllow(r[0])}>Remove</button></td>
								</tr>
							)) : (
								<tr><td colSpan={isProvider ? 5 : 4} className={styles.empty}>No allowlist entries yet.</td></tr>
							)}
						</tbody>
					</table>
				</div>
			)}

			<div className={styles.tabs}>
				{order.map((key) => {
					const det = detectors[key];
					if (!det) return null;
					return (
						<button
							key={key}
							className={`${styles.tab} ${active === key ? styles.active : ''}`}
							onClick={() => setActive(key)}
						>
							{det.label}
							<span className={styles.tier}>{det.tier}</span>
							<span className={styles.count}>{det.count}</span>
						</button>
					);
				})}
			</div>

			<div className={styles.intro}>{INTRO[active]}</div>

			<div className={styles.header_controls}>
				<div className={styles.control}>
					<SelectBox label='Watcher' value={selectedWatcher} onChange={(e) => setSelectedWatcher(e.target.value)}>
						<option value=''>Select a Watcher</option>
						<option value='All Watchers'>All Watchers</option>
						{watchers.map((w) => (w.length >= 2 ? (
							<option value={w[0]} key={w[0]}>{w[1]}</option>
						) : null))}
					</SelectBox>
				</div>
			</div>

			{loading ? (
				<Loader />
			) : hasError && summary === null ? (
				<div>
					<NetworkError />
					{supportErrorMessage && (
						<div className={styles.supportErrorCard}>
							<strong>Support Error:</strong>
							<code className={styles.supportErrorCode}>{supportErrorMessage}</code>
						</div>
					)}
				</div>
			) : (
				<div className={styles.table_container}>
					<Table
						containerStyle={{ height: '100%', flex: '1 1 auto', overflow: 'auto' }}
						tableHead={<tr>{cols.map((c) => <SortableTh key={c.h} label={c.h} field={c.f} sort={sort} />)}<SortableTh label='Watcher' field='watcher' sort={sort} /><th></th></tr>}
						tableRow={
							pageRows.length > 0 ? (
								pageRows.map((f, i) => (
									<tr key={`${active}-${i}`}>
										{cols.map((c) => (
											<td
												key={c.h}
												className={`${c.mono ? styles.mono : ''} ${c.wrap ? styles.wrap_cell : ''}`.trim()}
											>
												{c.sev ? (
													<span className={`${styles.sevBadge} ${sevClass(f[c.f])}`}>{f[c.f]}</span>
												) : c.zone ? (
													<ZoneFlow f={f} />
												) : (
													f[c.f] === undefined || f[c.f] === null || f[c.f] === '' ? '—' : String(f[c.f])
												)}
											</td>
										))}
										<td>{f.watcher}</td>
										<td>
											<button className={styles.row_allow} title='Add to allowlist' onClick={() => quickAllow(f)}>
												⊘ Allow
											</button>
										</td>
									</tr>
								))
							) : (
								<tr>
									<td colSpan={cols.length + 2} className={styles.empty}>
										No findings for this detector on the selected watcher.
									</td>
								</tr>
							)
						}
					/>
					<div className='paginationContainer'>
						<Pagination
							onPageChange={handlePageChange}
							rowsPerPage={pageLimit}
							totalPageSize={totalRows}
							pageNumber={pageNumber}
						/>
					</div>
				</div>
			)}
		</Content>
	);
};

export default SocAnalyticsScreen;
